import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Button, IconButton, Pagination, Slider, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import '../css/AvailableMissions.css';
import API_BASE_URL from '../api';

const ITEMS_PER_PAGE = 8;

const AvailableMissions = () => {
  const [missions, setMissions] = useState([]);
  const [filteredMissions, setFilteredMissions] = useState([]);
  const [skillsOptions, setSkillsOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [availability, setAvailability] = useState('');
  const [experience, setExperience] = useState('');
  const [maxTJM, setMaxTJM] = useState(1800);
  const [location, setLocation] = useState('');
  const [workMode, setWorkMode] = useState('');

  useEffect(() => {
    const fetchMissions = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/missions`, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const publishedMissions = response.data.filter(mission => mission.status === 'published');
        const sortedMissions = publishedMissions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setMissions(sortedMissions);
        setFilteredMissions(sortedMissions);

        const skills = new Set();
        sortedMissions.forEach(mission => {
          mission.skills.forEach(skill => skills.add(skill));
        });

        const skillsOptions = Array.from(skills).map(skill => ({ value: skill, label: skill }));
        setSkillsOptions(skillsOptions);
      } catch (error) {
        setError('Erreur lors de la récupération des missions');
      }
    };

    fetchMissions();
  }, []);

  const handleSkillChange = (selectedOptions) => {
    filterMissions({ skills: selectedOptions.map(option => option.value) });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    filterMissions({ searchTerm: e.target.value });
  };

  const handleAvailabilityChange = (e) => {
    setAvailability(e.target.value);
    filterMissions({ availability: e.target.value });
  };

  const handleExperienceChange = (e) => {
    setExperience(e.target.value);
    filterMissions({ experience: e.target.value });
  };

  const handleLocationChange = (e) => {
    setLocation(e.target.value);
    filterMissions({ location: e.target.value });
  };

  const handleWorkModeChange = (e) => {
    setWorkMode(e.target.value);
    filterMissions({ workMode: e.target.value });
  };

  const handleTJMChange = (event, newValue) => {
    setMaxTJM(newValue);
    filterMissions({ maxTJM: newValue });
  };

  const filterMissions = (filter = {}) => {
    let updatedMissions = missions;

    if (filter.skills) {
      updatedMissions = updatedMissions.filter(mission => filter.skills.every(skill => mission.skills.includes(skill)));
    }

    if (filter.searchTerm) {
      updatedMissions = updatedMissions.filter(mission => mission.name.toLowerCase().includes(filter.searchTerm.toLowerCase()));
    }

    if (filter.availability) {
      updatedMissions = updatedMissions.filter(mission => mission.availability === filter.availability);
    }

    if (filter.experience) {
      updatedMissions = updatedMissions.filter(mission => mission.experience === filter.experience);
    }

    if (filter.location) {
      updatedMissions = updatedMissions.filter(mission => mission.location.toLowerCase().includes(filter.location.toLowerCase()));
    }

    if (filter.workMode) {
      updatedMissions = updatedMissions.filter(mission => mission.workMode === filter.workMode);
    }

    if (filter.maxTJM) {
      updatedMissions = updatedMissions.filter(mission => mission.budgetTJM <= filter.maxTJM);
    }

    setFilteredMissions(updatedMissions);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentMissions = filteredMissions.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <Box className="available-missions-container" sx={{ display: 'flex' }}>
      <Box className="sidebar" sx={{ width: '250px', bgcolor: 'white', p: 2, boxShadow: 1 }}>
        <Typography variant="h6" gutterBottom>
          Navigation
        </Typography>
        <Button component={Link} to="/profiles" fullWidth variant="contained" color="primary" sx={{ mb: 2 }}>
          Profils disponibles
        </Button>
        <Button component={Link} to="/missions-list" fullWidth variant="contained" color="secondary">
          Missions disponibles
        </Button>
        <Typography variant="h6" gutterBottom mt={4}>
          Filtres de recherche
        </Typography>
        <FormControl fullWidth margin="normal">
          <InputLabel>Disponibilité</InputLabel>
          <Select value={availability} onChange={handleAvailabilityChange}>
            <MenuItem value=""><em>Aucune</em></MenuItem>
            <MenuItem value="Immediate">Immédiate</MenuItem>
            <MenuItem value="1 week">1 semaine</MenuItem>
            <MenuItem value="1 month">1 mois</MenuItem>
            <MenuItem value="3 months">3 mois</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Expérience</InputLabel>
          <Select value={experience} onChange={handleExperienceChange}>
            <MenuItem value=""><em>Aucune</em></MenuItem>
            <MenuItem value="Moins de 2 ans">Moins de 2 ans</MenuItem>
            <MenuItem value="2 à 5 ans">2 à 5 ans</MenuItem>
            <MenuItem value="5 à 10 ans">5 à 10 ans</MenuItem>
            <MenuItem value="Plus de 10 ans">Plus de 10 ans</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Lieu d'exécution</InputLabel>
          <TextField value={location} onChange={handleLocationChange} />
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel>Mode de travail</InputLabel>
          <Select value={workMode} onChange={handleWorkModeChange}>
            <MenuItem value=""><em>Aucune</em></MenuItem>
            <MenuItem value="Présentiel">100% Présentiel</MenuItem>
            <MenuItem value="Télétravail">100% Télétravail</MenuItem>
            <MenuItem value="Partiel">Télétravail partiel</MenuItem>
          </Select>
        </FormControl>
        <Typography gutterBottom>Tarification TJM max</Typography>
        <Slider
          value={maxTJM}
          onChange={handleTJMChange}
          aria-labelledby="tarification-slider"
          step={50}
          min={0}
          max={1800}
          valueLabelDisplay="auto"
        />
        <Typography gutterBottom>Compétences</Typography>
        <CreatableSelect
          isMulti
          options={skillsOptions}
          onChange={handleSkillChange}
          placeholder="Rechercher par compétences"
          className="skills-select"
        />
      </Box>
      <Box className="main-content" sx={{ flex: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {filteredMissions.length} Missions disponibles
        </Typography>
        <Box className="controls" sx={{ display: 'flex', mb: 3, gap: 2 }}>
          <TextField 
            variant="outlined" 
            placeholder="Rechercher des missions" 
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon />
                </IconButton>
              )
            }}
            fullWidth
          />
        </Box>
        {error && <Typography color="error">{error}</Typography>}
        <TableContainer component={Paper}>
          <Table className="mission-table">
            <TableHead>
              <TableRow>
                <TableCell>Réf.</TableCell>
                <TableCell>Titre</TableCell>
                <TableCell>TJM</TableCell>
                <TableCell>Début</TableCell>
                <TableCell>Lieu</TableCell>
                <TableCell>Publié le</TableCell>
                <TableCell>-</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentMissions.map((mission) => (
                <TableRow key={mission._id}>
                  <TableCell>{mission.reference}</TableCell>
                  <TableCell>{mission.name}</TableCell>
                  <TableCell>{`${mission.budgetTJM}€`}</TableCell>
                  <TableCell>{mission.startDate ? new Date(mission.startDate).toLocaleDateString() : 'ASAP'}</TableCell>
                  <TableCell>{mission.location}</TableCell>
                  <TableCell>{new Date(mission.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button component={Link} to={`/missions/${mission._id}`} variant="contained" color="primary">
                      Détails
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(filteredMissions.length / ITEMS_PER_PAGE)}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AvailableMissions;
